import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/hooks/useApi'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import type { Proyecto, Tarea, BacklogTask } from '@/lib/types'
import { PageHeader } from '@/components/ui'
import { IconKanban, IconList } from '@/components/icons/Icons'

/* ─── helpers ─── */
function formatHours(h: number): string {
  if (h === 0) return '0h'
  if (h < 1) return `${Math.round(h * 60)}min`
  return `${Math.round(h * 10) / 10}h`
}

const prioridadColor: Record<string, string> = {
  urgente: 'var(--danger)',
  alta:    '#f59e0b',
  media:   'var(--primary)',
  baja:    'var(--text-secondary)',
}

const estadoTareaLabel: Record<string, string> = {
  pendiente:   'Pendiente',
  en_progreso: 'En Progreso',
  completada:  'Completada',
  bloqueada:   'Bloqueada',
}

export default function DashboardOperaciones() {
  const api = useApi()
  const { data: proyectos = [], isLoading: l1 } = useCachedQuery<Proyecto>('proyectos')
  const { data: tareas = [], isLoading: l2 } = useCachedQuery<Tarea>('tareas')
  const [backlog, setBacklog] = useState<BacklogTask[]>([])
  const [l3, setL3] = useState(true)

  useEffect(() => {
    api.get<BacklogTask[]>('/api/backlog')
      .then(data => setBacklog(data))
      .catch(() => {})
      .finally(() => setL3(false))
  }, [])

  const loading = l1 || l2 || l3

  /* ─── KPIs ─── */
  const kpis = useMemo(() => {
    const activos = proyectos.filter(p => p.estado === 'activo').length
    const pausados = proyectos.filter(p => p.estado === 'pausado').length
    const completados = proyectos.filter(p => p.estado === 'completado').length
    const total = proyectos.length

    const tareasTotal = tareas.length
    const tareasPendientes = tareas.filter(t => t.estado === 'pendiente').length
    const tareasEnProgreso = tareas.filter(t => t.estado === 'en_progreso').length
    const tareasCompletadas = tareas.filter(t => t.estado === 'completada').length
    const tareasBloqueadas = tareas.filter(t => t.estado === 'bloqueada').length

    const horasEstimadas = tareas.reduce((s, t) => s + (t.horas_estimadas || 0), 0)
    const horasReales = tareas.reduce((s, t) => s + (t.horas_reales || 0), 0)

    const backlogTotal = backlog.length
    const backlogPendientes = backlog.filter(b => b.estado === 'pendiente').length

    return {
      activos, pausados, completados, total,
      tareasTotal, tareasPendientes, tareasEnProgreso, tareasCompletadas, tareasBloqueadas,
      horasEstimadas, horasReales,
      backlogTotal, backlogPendientes,
    }
  }, [proyectos, tareas, backlog])

  /* ─── Distribución de tareas por estado (para gráfico) ─── */
  const taskDistribution = useMemo(() => {
    const data = [
      { label: 'Pendiente', count: kpis.tareasPendientes, color: 'var(--warning)' },
      { label: 'En Progreso', count: kpis.tareasEnProgreso, color: 'var(--primary)' },
      { label: 'Completada', count: kpis.tareasCompletadas, color: 'var(--success)' },
      { label: 'Bloqueada', count: kpis.tareasBloqueadas, color: 'var(--danger)' },
    ]
    const maxVal = Math.max(...data.map(d => d.count), 1)
    return { data, maxVal }
  }, [kpis])

  /* ─── Proyectos activos con métricas ─── */
  const proyectosActivos = useMemo(() => {
    return proyectos
      .filter(p => p.estado === 'activo')
      .map(p => {
        const tareasP = tareas.filter(t => t.proyecto_id === p.id)
        const completadas = tareasP.filter(t => t.estado === 'completada').length
        const totalT = tareasP.length
        const progreso = totalT > 0 ? Math.round((completadas / totalT) * 100) : 0
        return { ...p, totalTareas: totalT, completadas, progreso }
      })
      .sort((a, b) => b.totalTareas - a.totalTareas)
      .slice(0, 8)
  }, [proyectos, tareas])

  /* ─── Tareas recientes de alta prioridad ─── */
  const tareasUrgentes = useMemo(() => {
    return tareas
      .filter(t => (t.prioridad === 'urgente' || t.prioridad === 'alta') && t.estado !== 'completada')
      .sort((a, b) => {
        const prioOrder: Record<string, number> = { urgente: 0, alta: 1, media: 2, baja: 3 }
        return (prioOrder[a.prioridad] ?? 3) - (prioOrder[b.prioridad] ?? 3)
      })
      .slice(0, 8)
  }, [tareas])

  /* ─── Backlog items recientes ─── */
  const backlogRecientes = useMemo(() => {
    return [...backlog]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 6)
  }, [backlog])

  if (loading) return (
    <div className="page-enter" style={{ padding: '2rem' }}>
      <PageHeader breadcrumbs={[{ label: 'Operaciones' }]} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter" id="dashboard-operaciones">
      <PageHeader
        breadcrumbs={[{ label: 'Operaciones' }]}
        actions={
          <>
            <Link to="/proyectos" className="btn btn-primary">
              <IconKanban size={16} /> Ver Proyectos
            </Link>
            <Link to="/backlog" className="btn btn-secondary">
              <IconList size={16} /> Backlog
            </Link>
          </>
        }
      />

      {/* ─── KPI Cards ─── */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <Link to="/proyectos" style={{ textDecoration: 'none' }}>
          <div className="stat-card">
            <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg>
              Proyectos Activos
            </div>
            <div className="stat-value success">{kpis.activos}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              {kpis.total} total · {kpis.pausados} pausados
            </div>
          </div>
        </Link>

        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
            Tareas En Progreso
          </div>
          <div className="stat-value" style={{ color: 'var(--primary)' }}>{kpis.tareasEnProgreso}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            {kpis.tareasPendientes} pendientes · {kpis.tareasBloqueadas} bloqueadas
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
            Tareas Completadas
          </div>
          <div className="stat-value success">{kpis.tareasCompletadas}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            de {kpis.tareasTotal} total
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Horas Registradas
          </div>
          <div className="stat-value" style={{ color: 'var(--warning)' }}>{formatHours(kpis.horasReales)}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
            de {formatHours(kpis.horasEstimadas)} estimadas
          </div>
        </div>

        <Link to="/backlog" style={{ textDecoration: 'none' }}>
          <div className="stat-card">
            <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--info, #60a5fa)" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              Backlog
            </div>
            <div className="stat-value" style={{ color: 'var(--info, #60a5fa)' }}>{kpis.backlogTotal}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 4 }}>
              {kpis.backlogPendientes} pendientes
            </div>
          </div>
        </Link>
      </div>

      {/* ─── Distribución de tareas por estado ─── */}
      <div className="card" style={{ marginTop: 20 }}>
        <div className="card-header">
          <h2>Distribución de Tareas por Estado</h2>
        </div>
        <div className="contabilidad-chart">
          {taskDistribution.data.map((d, i) => (
            <div key={i} className="contabilidad-chart-col">
              <div className="contabilidad-chart-bars">
                <div
                  className="contabilidad-bar"
                  style={{
                    height: `${(d.count / taskDistribution.maxVal) * 100}%`,
                    background: d.color,
                    minHeight: d.count > 0 ? 4 : 0,
                  }}
                  title={`${d.label}: ${d.count}`}
                />
              </div>
              <div className="contabilidad-chart-label">{d.label}</div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>{d.count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── Bottom: 3 columns ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 16 }}>
        {/* Proyectos activos */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Proyectos Activos</h2>
            <Link to="/proyectos" className="btn btn-sm btn-secondary">Ver todos</Link>
          </div>
          {proyectosActivos.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', padding: '1rem 0', textAlign: 'center', fontSize: '0.85rem' }}>
              Sin proyectos activos
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {proyectosActivos.map(p => (
                <Link key={p.id} to={`/proyectos/${p.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{p.nombre}</div>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                        {p.completadas}/{p.totalTareas}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${p.progreso}%`,
                          height: '100%',
                          background: p.progreso === 100 ? 'var(--success)' : 'var(--primary)',
                          borderRadius: 2,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 3 }}>
                      {p.progreso}% completado
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Tareas urgentes / alta prioridad */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Tareas Prioritarias</h2>
          </div>
          {tareasUrgentes.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', padding: '1rem 0', textAlign: 'center', fontSize: '0.85rem' }}>
              Sin tareas urgentes o de alta prioridad
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {tareasUrgentes.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.titulo}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
                      <span style={{
                        fontSize: '0.65rem',
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: `${prioridadColor[t.prioridad]}20`,
                        color: prioridadColor[t.prioridad],
                        fontWeight: 700,
                        textTransform: 'uppercase',
                      }}>
                        {t.prioridad}
                      </span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                        {estadoTareaLabel[t.estado] || t.estado}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Backlog reciente */}
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Backlog Reciente</h2>
            <Link to="/backlog" className="btn btn-sm btn-secondary">Ver todo</Link>
          </div>
          {backlogRecientes.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', padding: '1rem 0', textAlign: 'center', fontSize: '0.85rem' }}>
              Backlog vacío
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {backlogRecientes.map(b => (
                <div key={b.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.titulo}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 3 }}>
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: `${prioridadColor[b.prioridad] || 'var(--text-secondary)'}20`,
                      color: prioridadColor[b.prioridad] || 'var(--text-secondary)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}>
                      {b.prioridad}
                    </span>
                    <span style={{
                      fontSize: '0.65rem',
                      padding: '1px 6px',
                      borderRadius: 4,
                      background: 'var(--veil-06)',
                      color: 'var(--text-secondary)',
                    }}>
                      {b.tipo}
                    </span>
                    {b.contacto_nombre && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                        · {b.contacto_nombre}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
